// IMP-151 AC-7: PC 활성화 thunk 결과/에러 컨벤션 SSoT.
//
// 두 가지 thunk 컨벤션:
//   - Query (fetch): data | null  — Check*/Get* (Redux dispatch + 데이터 반환, 실패 시 null)
//   - Command (mutation): Result  — Activate/Deactivate/Suspend/Resume (아래 형태)
// 본 모듈은 Command 결과 생성과 reason enum 을 표준화한다(형태는 기존과 동일).

// IMP-421: 실패 code → 안내 문구 키 매핑은 licenseCodes SSoT. (licenseCodes 는 import 가 없어
// 순환 참조 위험 없음.)
import { getLicenseCodeMessageKey } from './licenseCodes';

// 실패 reason SSoT (기존 산재 문자열 리터럴 통합).
// PC 활성화(rayteams gateway/local) thunk 전용 reason 집합 — SmartDentAction 의 BE-axios reason 과 별개.
export const PC_FAIL_REASON = Object.freeze({
    NETWORK_ERROR: 'NETWORK_ERROR',
    INVALID_PARAMS: 'INVALID_PARAMS',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    PHANTOM_PC: 'PHANTOM_PC',
    STALE_USED_LIST: 'STALE_USED_LIST',
    UNAUTHORIZED: 'UNAUTHORIZED',
    GATEWAY_ERROR: 'GATEWAY_ERROR',
});

// IMP-421 (#356, PR #542 리뷰 Minor): reason → 사용자 안내 문구 i18n 키. code 계열만 번역되고
// reason 계열(`NETWORK_ERROR` 등)은 원문이 그대로 노출되던 것을 함께 해소한다.
// 미정의 reason 은 `null`(fail-closed) — 호출처가 일반 안내로 폴백한다.
const PC_FAIL_REASON_MESSAGE_KEYS = Object.freeze({
    NETWORK_ERROR: 'pcActivation.reason.NETWORK_ERROR',
    INVALID_PARAMS: 'pcActivation.reason.INVALID_PARAMS',
    UNAUTHENTICATED: 'pcActivation.reason.UNAUTHENTICATED',
    PHANTOM_PC: 'pcActivation.reason.PHANTOM_PC',
    STALE_USED_LIST: 'pcActivation.reason.STALE_USED_LIST',
    UNAUTHORIZED: 'pcActivation.reason.UNAUTHORIZED',
    GATEWAY_ERROR: 'pcActivation.reason.GATEWAY_ERROR',
});

export const getPcFailReasonMessageKey = (reason) => (
    typeof reason === 'string' && Object.prototype.hasOwnProperty.call(PC_FAIL_REASON_MESSAGE_KEYS, reason)
        ? PC_FAIL_REASON_MESSAGE_KEYS[reason]
        : null
);

// IMP-421 (#356, PR #542 R2 리뷰 Major): "실패 result → 표시 문구" 결정 SSoT.
// R1 에서는 조합 헬퍼를 usePcActivation.js(page hook) 모듈 스코프에 뒀는데, 그 결과
// ① 결정이 lib 2개 + hook 1개로 3분할되고 ② 같은 실패를 표시하는 LicenseDetailModal 이
// import 할 수 없어 그 화면에는 reason 원문(`STALE_USED_LIST` 등)이 남았다.
//
// 우선순위: 코드 매핑 문구 → reason 매핑 문구 → 코드 원문 + 일반 안내 → 일반 안내.
// 코드 원문 폴백은 원인 추적을 위해 남기지만, `code` 는 신뢰 경계 밖(localhost:8008 rayteams
// 응답)이므로 **코드표 형식(대문자 1 + 숫자 4)만 통과**시킨다 — 임의 문자열·객체가 배너에
// 그대로 보간되는 것을 막는다(actions/PcActivationAction.js:327-335 의 code.slice(0,50) /
// pages/admin/license/LicenseDetailModal.js:107,155 의 MAX_REASON_LEN 규약과 같은 취지).
const LICENSE_CODE_SHAPE = /^[A-Z]\d{4}$/;

// 표시 문구를 i18n 키 + (선택) 코드 접두로 분해한 순수 함수 — lib 은 i18n 에 결합하지 않는다.
// 매핑이 없으면 `key: null` — 호출처가 자기 화면의 일반 안내 문구를 그대로 유지한다.
export const resolvePcFailMessage = (result) => {
    const codeKey = getLicenseCodeMessageKey(result?.code);
    if (codeKey) return { key: codeKey, code: null };
    const reasonKey = getPcFailReasonMessageKey(result?.reason);
    if (reasonKey) return { key: reasonKey, code: null };

    const rawCode = result?.code;
    const shapedCode = (typeof rawCode === 'string' && LICENSE_CODE_SHAPE.test(rawCode)) ? rawCode : null;
    // silent-drop 금지 (CLAUDE.md §7): 매핑 실패로 code/reason 원문을 화면에서 제거하는 경로다.
    // 로그가 없으면 rayteams 코드표 drift 시 사용자·개발자 양쪽에서 원인 신호가 소실된다.
    console.warn('[IMP-421] unmapped PC activation failure', {
        resolver: 'resolvePcFailMessage',
        code: String(rawCode).slice(0, 50),
        reason: String(result?.reason).slice(0, 50),
        shown: shapedCode ? 'code + generic' : 'generic only',
    });
    return { key: null, code: shapedCode };
};

// 렌더 측 조합 — `translate` 를 주입받아 lib 의 i18n 무결합을 유지하면서 포맷을 한 곳에 둔다.
// `fallbackKey` 는 매핑 부재 시 쓸 화면별 일반 안내(기본 = 활성화 배너의 기존 문구).
export const resolvePcFailText = (result, translate, fallbackKey = 'common.status.error') => {
    const { key, code } = resolvePcFailMessage(result);
    const text = translate(key || fallbackKey);
    return code ? `${code} — ${text}` : text;
};

// 성공 result — { ...extra, ok: true }. extra(status/updatedCount/alreadyRevoked 등) 가
// ok 를 덮어쓰지 못하도록 ok 를 마지막에 둬 항상 true 보장(향후 오용 방지).
export const thunkOk = (extra = {}) => ({ ...extra, ok: true });

// 실패 result(reason 기반) — { ok: false, reason }.
export const thunkFail = (reason) => ({ ok: false, reason });

// 실패 result(code 기반) — { ok: false, code }. ActivateCurrentPC 의 payload.code 경로.
export const thunkFailCode = (code) => ({ ok: false, code });

// gateway(DoDB) 에러 → reason 매핑 SSoT. rayteams gateway(DoDB) 경유 thunk 전용
// (DeactivateOtherPC/Suspend/ResumeVoucherLicenseDdb). 401/403 → UNAUTHORIZED, 그 외 →
// GATEWAY_ERROR. (ConditionalCheckFailed 등 호출자 고유 분기는 이 헬퍼 호출 전 선처리 — 동작 불변.)
// ⚠️ SmartDentAction 의 BE-axios(api-server) thunk 에는 적용 금지 — 그쪽은 409/404/403/401
//    등 더 풍부한 vocabulary 를 별도로 사용한다(이 2분 분기로 치환 시 silent 의미변화).
export const mapGatewayError = (error) => {
    const status = error?.response?.status;
    return (status === 401 || status === 403)
        ? PC_FAIL_REASON.UNAUTHORIZED
        : PC_FAIL_REASON.GATEWAY_ERROR;
};
